import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Configure Supabase client with improved settings
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storage: window.localStorage,
    storageKey: 'ai-agency-hub-auth',
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 1
    }
  },
  global: {
    headers: {
      'x-client-info': 'ai-agency-hub/1.0'
    }
  },
  db: {
    schema: 'public'
  },
  // Add fetch options to handle CORS and credentials
  fetch: (url, options = {}) => {
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'Access-Control-Allow-Origin': '*',
      }
    });
  }
});

// Improved retry mechanism with exponential backoff and better error handling
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    errorMessage?: string;
    shouldRetry?: (error: any) => boolean;
  } = {}
): Promise<T> {
  const {
    maxRetries = 10,
    initialDelay = 1000,
    maxDelay = 30000,
    errorMessage = 'Operation failed',
    shouldRetry = (error: any) => {
      // Don't retry authentication errors
      if (error?.message?.includes('JWT expired')) {
        return false;
      }
      // Don't retry permission errors
      if (error?.message?.includes('permission denied')) {
        return false;
      }
      // Don't retry stack depth limit errors
      if (error?.code === '54001' || error?.message?.includes('stack depth limit exceeded')) {
        return false;
      }
      // Always retry network errors with increased persistence
      if (
        error instanceof TypeError && 
        (error.message.includes('Failed to fetch') || 
         error.message.includes('NetworkError') ||
         error.message.includes('Network request failed'))
      ) {
        return true;
      }
      // Always retry timeout errors
      if (error?.code === '57014' || error?.message?.includes('timeout')) {
        return true;
      }
      // Don't retry CORS errors
      if (error?.message?.includes('Failed to read') || error?.message?.includes('cross-origin')) {
        return false;
      }
      // Don't retry 404 errors
      if (error?.status === 404 || error?.message?.includes('not found')) {
        return false;
      }
      // Retry server errors (500+)
      if (error?.status >= 500) {
        // But don't retry stack depth limit errors
        if (error?.body?.includes('stack depth limit exceeded')) {
          return false;
        }
        return true;
      }
      return false;
    }
  } = options;

  let lastError: Error | undefined;
  let attempt = 0;

  while (attempt < maxRetries) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error instanceof Error ? error : new Error(
        typeof error === 'object' ? 
          error?.message || error?.error_description || JSON.stringify(error) : 
          String(error)
      );
      
      // Extract detailed error information
      const errorDetails = {
        code: error?.code,
        status: error?.status,
        body: error?.body,
        message: lastError.message
      };

      // Check if we should retry this error
      if (!shouldRetry(error)) {
        console.error('Non-retryable error:', errorDetails);
        throw lastError;
      }

      // Don't log first attempt failures for network errors
      if (!(attempt === 0 && lastError.message.includes('Failed to fetch'))) {
        console.warn(`Attempt ${attempt + 1} failed:`, errorDetails);
      }
      
      if (attempt < maxRetries - 1) {
        // Calculate delay with exponential backoff and jitter
        const delay = Math.min(
          initialDelay * Math.pow(1.5, attempt) + Math.random() * 1000,
          maxDelay
        );
        
        // Log retry attempt if it's not a common network error
        if (!lastError.message.includes('Failed to fetch')) {
          console.log(`Retrying in ${Math.round(delay)}ms...`);
        }
        
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
      } else {
        break;
      }
    }
  }
  
  console.error(`${errorMessage} after ${maxRetries} attempts:`, lastError?.message);
  throw lastError || new Error(`${errorMessage} after ${maxRetries} attempts`);
}

// Add health check function to verify Supabase connection
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    await withRetry(
      async () => await supabase.from('users').select('count').limit(1),
      {
        maxRetries: 3,
        initialDelay: 1000,
        errorMessage: 'Failed to connect to Supabase'
      }
    );
    return true;
  } catch (error) {
    console.error('Supabase connection check failed:', error);
    return false;
  }
}